import { getFilmFormat } from '@/content/instantFilm'
import type { CompositionNode, FrameNode } from '@/core/document'
import type { Size } from '@/core/geometry'
import type { RenderContext } from '@/core/render/renderNode'
import {
  FrameFontFamily,
} from '@/tools/frame/fonts'
import {
  PRINT_SHADOW,
  WINDOW_SHADOW,
  PhotoPlacement,
  formatDate,
  inkColorFor,
  type PolaroidFrameParams,
} from '@/tools/frame/params'

type PolaroidFrameNode = Omit<FrameNode, 'params'> & { params: PolaroidFrameParams }

const STAMP_COLOR = '#ff8a1e'
const STAMP_ALPHA = 0.6

export type RenderFrameChild = (
  child: CompositionNode,
  targetSize: Size,
  context: RenderContext,
) => void

function ellipsize(context: RenderContext, value: string, maxWidth: number): string {
  if (context.measureText(value).width <= maxWidth) return value
  const suffix = '…'
  let result = value
  while (result.length > 0 && context.measureText(`${result}${suffix}`).width > maxWidth) {
    result = result.slice(0, -1)
  }
  return `${result}${suffix}`
}

function drawWindowShadow(
  context: RenderContext,
  window: { x: number; y: number; width: number; height: number },
  pixelsPerMm: number,
): void {
  context.save()
  context.beginPath()
  context.rect(window.x, window.y, window.width, window.height)
  context.clip()
  context.shadowColor = `rgb(0 0 0 / ${WINDOW_SHADOW.alpha})`
  context.shadowBlur = WINDOW_SHADOW.blurMm * pixelsPerMm
  context.lineWidth = Math.max(1, WINDOW_SHADOW.spreadMm * pixelsPerMm * 2)
  context.strokeStyle = `rgb(0 0 0 / ${WINDOW_SHADOW.alpha})`
  context.strokeRect(window.x, window.y, window.width, window.height)
  context.restore()
}

function drawCaption(
  context: RenderContext,
  node: PolaroidFrameNode,
  area: { x: number; y: number; width: number; height: number },
): void {
  const { params } = node
  if (!params.caption && (!params.date || !params.showWrittenDate)) return
  context.save()
  context.translate(area.x, area.y)
  const { width, height } = area
  context.beginPath()
  context.rect(0, 0, width, height)
  context.clip()
  context.fillStyle = inkColorFor(params.paperColor)
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  const lines = [
    params.caption,
    params.date && params.showWrittenDate ? formatDate(params.date, params.dateFormatId) : '',
  ].filter(Boolean)
  const lineHeight = height / Math.max(lines.length, 1)
  const fontSize = Math.max(1, lineHeight * 0.82)
  context.font = `400 ${fontSize}px "${FrameFontFamily.Handwriting}"`
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]
    if (!line) continue
    context.fillText(
      ellipsize(context, line, width * 0.94),
      width / 2,
      lineHeight * (index + 0.5),
      width * 0.94,
    )
  }
  context.restore()
}

function drawStamp(
  context: RenderContext,
  node: PolaroidFrameNode,
  window: { x: number; y: number; width: number; height: number },
): void {
  const { params } = node
  if (!params.date || !params.showStampDate) return
  const text = formatDate(params.date, params.dateFormatId)
  const inset = Math.min(window.width, window.height) * 0.05
  context.save()
  context.globalAlpha = STAMP_ALPHA
  context.fillStyle = STAMP_COLOR
  context.textAlign = 'right'
  context.textBaseline = 'bottom'
  context.font = `400 ${Math.max(1, window.height * 0.055)}px "${FrameFontFamily.FilmStamp}"`
  context.fillText(text, window.x + window.width - inset, window.y + window.height - inset, window.width * 0.55)
  context.restore()
}

export function renderPolaroid(
  node: PolaroidFrameNode,
  childNativeSize: Size,
  targetSize: Size,
  context: RenderContext,
  renderChild: RenderFrameChild,
): void {
  const { params } = node
  const format = getFilmFormat(params.formatId)
  const pixelsPerMm = targetSize.width / format.filmMm.width
  const filmHeight = format.filmMm.height * pixelsPerMm
  if (Math.abs(filmHeight - targetSize.height) > 1) {
    throw new Error(`Polaroid frame target does not match the "${format.id}" physical ratio`)
  }
  const window = {
    x: format.windowMm.x * pixelsPerMm,
    y: format.windowMm.y * pixelsPerMm,
    width: format.windowMm.width * pixelsPerMm,
    height: format.windowMm.height * pixelsPerMm,
  }
  const captionArea = {
    x: format.captionAreaMm.x * pixelsPerMm,
    y: format.captionAreaMm.y * pixelsPerMm,
    width: format.captionAreaMm.width * pixelsPerMm,
    height: format.captionAreaMm.height * pixelsPerMm,
  }

  if (params.printShadow) {
    context.save()
    context.shadowColor = `rgb(0 0 0 / ${PRINT_SHADOW.alpha})`
    context.shadowBlur = PRINT_SHADOW.blurMm * pixelsPerMm
    context.shadowOffsetY = PRINT_SHADOW.offsetYMm * pixelsPerMm
    context.fillStyle = params.paperColor
    context.fillRect(0, 0, targetSize.width, targetSize.height)
    context.restore()
  }
  context.fillStyle = params.paperColor
  context.fillRect(0, 0, targetSize.width, targetSize.height)
  context.fillStyle = params.paperColor
  context.fillRect(window.x, window.y, window.width, window.height)

  context.save()
  context.beginPath()
  context.rect(window.x, window.y, window.width, window.height)
  context.clip()
  if (params.placement === PhotoPlacement.Fill) {
    context.translate(window.x, window.y)
    renderChild(node.child, { width: window.width, height: window.height }, context)
  } else {
    if (!params.free) throw new Error('Free Polaroid placement is missing its placement parameters')
    const photoWidth = window.width * params.free.scale
    const photoHeight = photoWidth * childNativeSize.height / childNativeSize.width
    const centerX = window.x + window.width * (0.5 + params.free.offsetX)
    const centerY = window.y + window.height * (0.5 + params.free.offsetY)
    context.translate(centerX - photoWidth / 2, centerY - photoHeight / 2)
    renderChild(node.child, { width: photoWidth, height: photoHeight }, context)
  }
  context.restore()

  if (params.windowShadow) drawWindowShadow(context, window, pixelsPerMm)
  drawStamp(context, node, window)
  drawCaption(context, node, captionArea)
}
